import { ProtoBaseModel, attr, repeated } from '@square/dashboard-data/utils/ember-model-stubs';
import MoneyProto from '@square/dashboard-data/models/squareup/common/money-proto';
import Enum from '@square/dashboard-utilities/utils/enum';
import { DeviceType } from '@square/dashboard-utilities/utils/devices/device-type';
import KdsLayout from '@square/dashboard-data/models/kds-layout';
import KdsTimerSetting from '@square/dashboard-data/models/kds-timer-setting';
import KdsCategoryConfiguration from '@square/dashboard-data/models/kds-category-configuration';
import KdsDiningOptionConfiguration from '@square/dashboard-data/models/kds-dining-option-configuration';
import KdsPosDeviceConfiguration from '@square/dashboard-data/models/kds-pos-device-configuration';
import { FNBKioskDiningOption } from '@square/dashboard-data/models/device-profiles-v2/settings/fnb-kiosk-settings';
import { CustomerIdentificationType } from '@square/dashboard-data/models/device-profiles-v2/settings/text-customer-settings';
const TrackingLevel = new Enum(['employee_management_tracking_level_complete', 'employee_management_tracking_level_restrict_actions']);
const InactivityTimeout = new Enum(['employee_management_inactivity_timeout_none', 'employee_management_inactivity_timeout_thirty_seconds', 'employee_management_inactivity_timeout_one_minute', 'employee_management_inactivity_timeout_five_minutes']);
const TippingCalculationPhase = new Enum(['PRE_TAX_TIP_CALCULATION', 'POST_TAX_TIP_CALCULATION']);
export const PrintedReceiptTypes = new Enum(['UNSET', 'TRADITIONAL', 'QUICK_TIP', 'GRATUITY_FREE']);
export const UserInterfaceStyle = new Enum(['DEFAULT', 'LIGHT', 'DARK']);
export const SeatAssignmentOption = new Enum(['DISABLED', 'OPTIONAL', 'REQUIRED']);
export const RequireSignatureOption = new Enum(['ALWAYS', 'ABOVE_AMOUNT', 'NEVER']);
export const RequireEmployeePasscodeOption = new Enum(['ALWAYS', 'RESTRICTED', 'NEVER']);
export const OrderTicketNamingType = new Enum(['NAMING_TYPE_UNSET', 'AUTOMATIC_NUMBERING', 'CUSTOM_NAME']);
export const DisplayOrderConfigurationState = new Enum(['DISPLAY_ORDER_CONFIGURATION_STATE_EVENT_DO_NOT_USE', 'ON_NEW', 'ON_MARKED_IN_PROGRESS']);
export const SoundType = new Enum(['SOUND_DO_NOT_USE', 'NEW_TICKET']);
export const TextSize = new Enum(['TEXT_SIZE_DO_NOT_USE', 'SMALL', 'MEDIUM', 'LARGE']);

/**
 * Remote device settings for point of sale devices
 *
 * The field names of the nested DeviceProfile.Settings proto: https://prototype.sqprod.co/#/docs/squareup.roster.deviceprofile.DeviceProfile.Settings
 * should match the names of the FlattenedSettings: https://prototype.sqprod.co/#/docs/squareup.devicesettings.credentials.http.FlattenedSettings
 * and named similiarly in this ProtoBaseModel. FlattenedSettings is the proto we use to save non-SPOS and shared settings.
 * Any naming mismatch will prevent the creation of new non-SPOS and shared settings.
 **/

export default ProtoBaseModel.extend({
  // Backend expects a device type
  type: attr(String, {
    defaultValue: DeviceType.REGISTER
  }),
  // Checkout options
  usePaperSignature: attr(Boolean, {
    defaultValue: false
  }),
  skipSignature: attr(Boolean, {
    defaultValue: false
  }),
  skipSignatureAlways: attr(Boolean, {
    defaultValue: false
  }),
  storeAndForwardEnabled: attr(Boolean, {
    defaultValue: false
  }),
  storeAndForwardSingleTransactionLimit: attr(String),
  skipReceiptScreen: attr(Boolean, {
    defaultValue: false
  }),
  forPaperSignatureUseQuickTipReceipt: attr(Boolean, {
    defaultValue: false
  }),
  paperSignatureTipType: attr(PrintedReceiptTypes, {
    defaultValue: PrintedReceiptTypes.UNSET
  }),
  forPaperSignaturePrintAdditionalAuthSlip: attr(Boolean, {
    defaultValue: false
  }),
  forPaperSignaturePrintItemizedAuthSlip: attr(Boolean, {
    defaultValue: false
  }),
  orderTicketNamingType: attr(OrderTicketNamingType, {
    defaultValue: OrderTicketNamingType.CUSTOM_NAME
  }),
  useBuyerFacingScreen: attr(Boolean, {
    defaultValue: true
  }),
  showBuyerFacingOrderSummary: attr(Boolean, {
    defaultValue: true
  }),
  // Cash management settings
  useCashDrawers: attr(Boolean, {
    defaultValue: false
  }),
  defaultStartingAmount: attr(MoneyProto),
  printReport: attr(Boolean, {
    defaultValue: false
  }),
  emailReport: attr(Boolean, {
    defaultValue: true
  }),
  recipientAddress: attr(String),
  // Customer management settings
  collectBeforeCheckout: attr(Boolean, {
    defaultValue: false
  }),
  collectAfterCheckout: attr(Boolean, {
    defaultValue: false
  }),
  useCardOnFile: attr(Boolean, {
    defaultValue: false
  }),
  showSaveCardButtonAfterCheckout: attr(Boolean, {
    defaultValue: false
  }),
  // The long-term plan for defaults is to overlay each device profile over its vertical-specific
  // defaults retrieved by the DefaultDeviceProfiles service. However, because that work is only
  // done for new devices (not existing devices) at this time, we need to enforce a default here for
  // older device profiles, for which this setting was not available whenn they were created.
  // TODO(RETAIL-7714): Remove this attribute from the device profile.
  showAddCustomerCartRow: attr(Boolean, {
    defaultValue: true
  }),
  showEmailCollectionScreen: attr(Boolean, {
    defaultValue: false
  }),
  // Text customer settings
  promptBeforeCheckout: attr(Boolean, {
    defaultValue: false
  }),
  customerPhoneNumberOptional: attr(Boolean, {
    defaultValue: false
  }),
  customerIdentificationType: attr(CustomerIdentificationType, {
    defaultValue: CustomerIdentificationType.CUSTOMER_NAME
  }),
  // Scan to pay settings
  printQrCodeOnReceipt: attr(Boolean, {
    defaultValue: false
  }),
  // Employee management settings
  trackEmployee: attr(Boolean, {
    defaultValue: true
  }),
  trackTime: attr(Boolean, {
    defaultValue: false
  }),
  inactivityTimeout: attr(InactivityTimeout, {
    defaultValue: InactivityTimeout.employee_management_inactivity_timeout_none
  }),
  trackingLevel: attr(TrackingLevel, {
    defaultValue: TrackingLevel.employee_management_tracking_level_complete
  }),
  requiresEmployeeLogoutAfterCompletedTransaction: attr(Boolean, {
    defaultValue: false
  }),
  requirePasscodeWhenBackingOutOfBuyerFacingScreen: attr(Boolean, {
    defaultValue: false
  }),
  // Hardware settings
  requireCardReader: attr(Boolean, {
    defaultValue: false
  }),
  requireCashDrawer: attr(Boolean, {
    defaultValue: false
  }),
  requirePrinter: attr(Boolean, {
    defaultValue: false
  }),
  // Open ticket settings
  openTicketsEnabled: attr(Boolean, {
    defaultValue: false
  }),
  useOpenTicketsMenuAsHomeScreen: attr(Boolean, {
    defaultValue: false
  }),
  usePredefinedTickets: attr(Boolean, {
    defaultValue: false
  }),
  // Tipping settings
  tippingEnabled: attr(Boolean, {
    defaultValue: false
  }),
  tippingUseCustomPercentages: attr(Boolean, {
    defaultValue: false
  }),
  tippingPercentageTipAmount: attr(Array, {
    defaultValue: null
  }),
  tippingUseManualTipEntry: attr(Boolean, {
    defaultValue: true
  }),
  tippingCalculationPhase: attr(TippingCalculationPhase, {
    defaultValue: TippingCalculationPhase.PRE_TAX_TIP_CALCULATION
  }),
  useSeparateTippingScreen: attr(Boolean, {
    defaultValue: false
  }),
  // Auto-Gratuity settings
  autoGratuityEnabled: attr(Boolean, {
    defaultValue: false
  }),
  // Seating settings
  seatingEnabled: attr(Boolean, {
    defaultValue: false
  }),
  // Seat Assignment Option
  seatAssignmentOption: attr(SeatAssignmentOption),
  // Course Management Settings
  courseManagementEnabled: attr(Boolean, {
    defaultValue: false
  }),
  expandFiredItemsSectionByDefault: attr(Boolean, {
    defaultValue: false
  }),
  kitchenControlsFire: attr(Boolean, {
    defaultValue: true
  }),
  // Floor Plan Settings
  showFloorPlanTimers: attr(Boolean, {
    defaultValue: false
  }),
  useTableInactivityIndicators: attr(Boolean, {
    defaultValue: false
  }),
  // Time (in minutes) after which a table with an inactive open ticket will first change to its "warning" color (yellow).
  // Must be < tableIndicatorAlertTime.
  tableIndicatorWarningTime: attr(Number),
  // Time (in minutes) after which a table with an inactive open ticket will change to its "alert" color (red).
  // Must be > tableIndicatorWarningTime.
  tableIndicatorAlertTime: attr(Number),
  // App Theme Settings
  userInterfaceStyle: attr(UserInterfaceStyle, {
    defaultValue: UserInterfaceStyle.DEFAULT
  }),
  // Order Hub Settings
  useOrderHub: attr(Boolean, {
    defaultValue: false
  }),
  // Orders Mode Settings
  ordersModeEnabled: attr(Boolean, {
    defaultValue: false
  }),
  // FNB Kiosk Settings
  allowGiftCards: attr(Boolean, {
    defaultValue: true
  }),
  diningOptions: repeated(FNBKioskDiningOption),
  // KDS Fetch Settings
  categories: repeated(KdsCategoryConfiguration),
  uncategorizedEnabled: attr(Boolean, {
    defaultValue: true
  }),
  includeFutureCategoriesEnabled: attr(Boolean, {
    defaultValue: true
  }),
  ordersEnabled: attr(Boolean, {
    defaultValue: true
  }),
  displayOrderOnState: attr(DisplayOrderConfigurationState, {
    defaultValue: DisplayOrderConfigurationState.ON_NEW
  }),
  posEnabled: attr(Boolean, {
    defaultValue: true
  }),
  stationOnPickupBufferSeconds: attr(Number),
  posDeviceConfigurations: repeated(KdsPosDeviceConfiguration),
  includeFutureNamedPosEnabled: attr(Boolean, {
    defaultValue: true
  }),
  includeUnnamedPosEnabled: attr(Boolean, {
    defaultValue: true
  }),
  diningOptionConfigurations: repeated(KdsDiningOptionConfiguration),
  includeFutureDiningOptionsEnabled: attr(Boolean, {
    defaultValue: true
  }),
  // KDS UI Settings
  timerSettings: repeated(KdsTimerSetting),
  enabledSounds: repeated(SoundType),
  layout: attr(KdsLayout),
  textSize: attr(TextSize, {
    defaultValue: TextSize.MEDIUM
  })
}).reopenClass({
  TrackingLevel,
  InactivityTimeout,
  TippingCalculationPhase,
  DEFAULT_WARNING_TIME: 45,
  DEFAULT_ALERT_TIME: 60,
  newCopyFromSettings(settings) {
    return this.deserialize(this.serialize(settings));
  }
});